import { IControl } from "Controls/Interfaces";
import { GridControlTypes, DisplayModes, CssClass, EncodingTypes } from "Enums";
import { IControlDefinition } from "Grids/Interfaces";

import { CoreContext, Util } from "Core";
import { EventHandler, EventArgs, EventFunction, EventObject } from "Core/Events";
import { TextBlockService } from "Service";
import { IDisposable } from "Core/Interfaces";

import { ControlEventTypes } from "Core/Events/EventTypes";
import { ControlTemplate } from "Controls/Entities";
import { DataConstants } from "Constants";
import { IDataRow } from "Entities";

/**
 * @hidden
 */
export const CONTROL_ID_ATTRIBUTE_NAME: string = "data-dms-id";

/**
 * @hidden
 */
export const CONTROL_VISIBILITY_PROPERTY_NAME: string = "__dms_isVisible_";

/**
 * @hidden
 */
export const CONTROL_FORMAT_PROPERTY_NAME: string = "__dms_cssClass_";

/**
 * Base class for all control types.
 */
export abstract class Control implements IControl, IDisposable {

    /**
     * The {@link EventHandler} used to register and trigger events for this control.
     * @hidden
     */
    protected eventHandler: EventHandler<IControl, ControlEventTypes>;

    /**
     * The {@link IControlDefinition} for this control.
     * @hidden
     */
    protected controlDefinition: IControlDefinition;

    /**
     * The original control definition.
     */
    private originalControlDefinition: IControlDefinition;

    /**
     * The jQueryElement which was created by the {@link BaseGrid} and passed to initialize the control.
     * @hidden
     */
    protected jQueryElement: JQuery;

    /**
     * The editor element which is used to initialize the control. (Default: <input />)
     * @hidden
     */
    protected defaultEditorElement: string = "<input />";

    /**
     * The binding property for the default binding. (Default: value).
     * @hidden
     */
    protected bindingProperty: string = "value";

    /**
     * Additional bindings for this control in the format used by kendo binding.
     * i.e. text:ColumnName
     * @hidden
     */
    protected additionalBinding: string = null;

    /**
     * The css class which should be unique for each control type.
     * @hidden
     */
    protected abstract dmsControlClass: string;

    /**
     * The category for textblocks used by this control.
     * @hidden
     */
    protected textBlockCategory: string;

    /**
     * Service to get textblocks.
     * @hidden
     */
    protected textBlockService: TextBlockService;

    // the radioButton-element if the control is part of a radiogroup.
    private radioButton: JQuery;

    /**
     * Initialize control.
     * @param controlDefinition
     */
    constructor(controlDefinition: IControlDefinition, textBlockCategory: string, textBlockService: TextBlockService) {
        this.controlDefinition = controlDefinition;
        this.textBlockCategory = textBlockCategory;
        this.textBlockService = textBlockService;
        this.eventHandler = new EventHandler<IControl, ControlEventTypes>(this, (sender, func) => this.resolveFunction(sender, func));
    }

    private resolveFunction(sender: IControl, func: string): string {
        return this.eventHandler.execute(sender, ControlEventTypes.Resolve, func) || func;
    }

    /**
     * Gets the Type of this control.
     * @since 4.0.0
     */
    public getControlType(): GridControlTypes {
        return this.controlDefinition.ControlTypeID;
    }

    /**
     * Get the DMS Control ID of this control.
     * @since 4.0.0
     */
    public getControlId(): number {
        return this.controlDefinition.ControlID;
    }

    /**
     * Gets the IControlDefinition from this control.
     * @hidden
     */
    public getControlDefinition(): IControlDefinition {
        return this.controlDefinition;
    }

    /**
     * Get value from the control.
     */
    public abstract getValue(): Object;

    /**
     * Set control value.
     * @param s Value to be set.
     */
    public abstract setValue(s: Object): void;

    /**
     * Trigger the eventHandler for given EventType.
     * @param eventType EventType to be triggered.
     * @param args Additional arguments (ie. EventArgs).
     * @internal
     */
    public trigger(eventType: ControlEventTypes, args?: any | EventArgs<IControl>) {
        this.eventHandler.trigger(this, eventType, args);
    }

    /**
     * Execute the script / function registered on EventType and returns the result.
     * @param eventType EventType to be triggered.
     * @param args Additional arguments (ie. EventArgs).
     * @hidden
     */
    public execute<TResult = any, TArgument = any>(eventType: ControlEventTypes, args?: TArgument): TResult {
        return this.eventHandler.execute<TResult, TArgument>(this, eventType, args);
    }

    /**
     * Register a function or script for given {@link ControlEventTypes}.
     * @param eventType EventType to be registered.
     * @param func Function or script to be registered.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * // register function
     * @@ctr:1337@@.on(DMS.Events.ControlEventTypes.Click, function() { alert('clicked') });
     *
     * // register function with EventArgs.
     * @@ctr:1337@@.on(DMS.Events.ControlEventTypes.Click, function(e) { alert(e.sender.getValue()); });
     *
     * // register script
     * @@ctr:1337@@.on(DMS.Events.ControlEventTypes.Click, "alert('clicked')");
     * ```
     */
    public on(eventType: ControlEventTypes, func: string | EventFunction<IControl> | EventObject<IControl>): void {
        return this.eventHandler.on(eventType, func);
    }

    /**
     * Register a function or script **for single execution** for given {@link ControlEventTypes}.
     * The handler is removed after it was triggered once.
     * @param eventType EventType to be registered.
     * @param func Function or script to be registered.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * // register function
     * @@ctr:1337@@.one(DMS.Events.ControlEventTypes.Click, function() { alert('clicked') });
     *
     * // register function with EventArgs.
     * @@ctr:1337@@.one(DMS.Events.ControlEventTypes.Click, function(e) { alert(e.sender.getValue()); });
     *
     * // register script
     * @@ctr:1337@@.one(DMS.Events.ControlEventTypes.Click, "alert('clicked')");
     * ```
     */
    public one(eventType: ControlEventTypes, func: string | EventFunction<IControl> | EventObject<IControl>): void {
        return this.eventHandler.one(eventType, func);
    }

    /**
     * Unregisters function or script from {@link ControlEventTypes}.
     * @param eventType EventType to be unregistered (omit to unregister all events on this control).
     * @param func Function or script to be unregistered (omit to unregister all scripts or functions for eventType).
     * @since 4.0.0
     * @example
     * ```JavaScript
     * // unregister click event
     * @@ctr:1337@@.off(DMS.Events.ControlEventTypes.Click);
     * ```
     */
    public off(eventType?: ControlEventTypes, func?: string | EventFunction<IControl>): void {
        this.eventHandler.off(eventType, func);
    }

    /**
     * Gets the HTMLElement which was used to initialize this control.
     * @since 4.0.0
     */
    public getElement(): HTMLElement {
        return this.jQueryElement[0];
    }

    /**
     * Resolve a script or link (process replacements with the current datarow).
     * @param script Script / Link to be resolved.
     * @hidden
     */
    protected resolve(script: string): string {
        if (this.eventHandler.get(ControlEventTypes.Resolve)) { // check if resolver is attached
            return this.execute<string, string>(ControlEventTypes.Resolve, script);
        }
        return script;
    }

    /**
     * Sets the JQuery Element after initializing the control.
     * @param element JQuery-Element.
     * @hidden
     */
    public setElement(element: JQuery): void {
        // if (this.jQueryElement != null) {
        //     this.dispose();
        // }

        // register handlers for script exection
        if (this.controlDefinition.BeforeExecuteScript) {
            this.eventHandler.on(ControlEventTypes.BeforeExecuteControl, new EventObject(this.controlDefinition.BeforeExecuteScript, null, true));
        }
        if (this.controlDefinition.AfterExecuteScript) {
            this.eventHandler.on(ControlEventTypes.AfterExecuteControl, new EventObject(this.controlDefinition.AfterExecuteScript, null, true));
        }
        if (this.controlDefinition.LoadScript) {
            this.eventHandler.on(ControlEventTypes.ControlLoaded, new EventObject(this.controlDefinition.LoadScript, null, true));
        }
        if (this.controlDefinition.LostFocusScript) {
            this.eventHandler.on(ControlEventTypes.LostFocus, new EventObject(this.controlDefinition.LostFocusScript, null, true));
        }
        if (this.controlDefinition.Script) {
            this.eventHandler.on(ControlEventTypes.Change, new EventObject(this.controlDefinition.Script, null, true));
        }

        // check if control was set required.
        let isRequired = element.hasClass("dms-required");
        if (isRequired) {
            element.removeClass("dms-required");
        }

        let isReadonly = element.hasClass("dms-readonly");
        if (isReadonly) {
            element.removeClass("dms-readonly");
        }

        this.jQueryElement = CoreContext.Instance.validateElement(element);

        this.setElementInternal(element);

        if (this.controlDefinition.Disabled) {
            this.disable();
        }

        // add the class to the input field
        element.addClass("dms-control-input");

        // get the wrapper-element
        let outerElement = this.getOuterElement();

        // add classes for styling and identification on the wrapper
        outerElement.addClass("dms-control");
        outerElement.addClass(this.dmsControlClass);

        if (isRequired) {
            outerElement.addClass("dms-required");
        }
        if (isReadonly) {
            outerElement.addClass("dms-readonly");
            outerElement.attr("tabindex", "-1");
        }

        if (this.controlDefinition.ToolTip && !(this.controlDefinition.UseValueColumnToolTip && (this.controlDefinition.ValueColumnName || this.controlDefinition.FilterParameterName))) {
            let toolTip = this.resolve(this.controlDefinition.ToolTip);
            if (toolTip == this.controlDefinition.ToolTip) { // tooltip was not changed in resolving -> tooltip is textblockname
                toolTip = this.textBlockService.get(this.controlDefinition.ToolTip, this.textBlockCategory);
            }
            outerElement.attr("data-dms-tooltip", Util.encode(toolTip, EncodingTypes.HtmlAttribute));
            outerElement.addClass("dms-tooltip");
        }

        if (this.controlDefinition.RadioGroup) {
            let wrapper = $("<div class='dms-radiogroup' />");
            this.radioButton = $(`<span><input type='radio' id='dms-radio-${this.controlDefinition.ControlID}' value='${this.controlDefinition.ControlID}' class='k-radio' name='${this.controlDefinition.RadioGroup}' /><label class='k-radio-label' for='dms-radio-${this.controlDefinition.ControlID}'></label></span>`);

            outerElement.parent().append(wrapper);
            wrapper.append(this.radioButton);
            wrapper.append(outerElement);
            this.radioButton.on("change", () => this.trigger(ControlEventTypes.RadioButtonChange));
        }
    }

    /**
     * Get the column name which contains the text for this control.
     * @hidden
     */
    public getTextColumnName(): string {
        return this.controlDefinition.ValueColumnName;
    }

    /**
     * If the control is part of a radioGroup, set the checked-value of the radiobutton for this control.
     * @param isChecked True if the radiobutton should be checked.
     * @since 4.0.0
     */
    public setRadioButtonChecked(isChecked: boolean): void {
        if (this.radioButton != null) {
            this.radioButton.find(".k-radio").removeAttr("data-bind");
            if (isChecked) {
                this.radioButton.find(".k-radio").prop("checked", isChecked);
                this.radioButton.find(".k-radio").trigger("change");
            }
        }
    }

    /**
     * Enable the control.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * @@ctr:1337@@.enable();
     * ```
     */
    public enable(): void {
        this.jQueryElement.prop("disabled", false);
        this.jQueryElement.removeClass("k-state-disabled");
    }

    /**
     * Disable the control.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * @@ctr:1337@@.disable();
     * ```
     */
    public disable(): void {
        this.jQueryElement.prop("disabled", true);
        this.jQueryElement.addClass("k-state-disabled");
    }

    /**
     * Returns true if control is enabled.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * var result = @@ctr:1337@@.isEnabled();
     * ```
     */
    public isEnabled(): boolean {
        return !this.jQueryElement.prop("disabled");
    }

    /**
     * Shows control if it was hidden.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * @@ctr:1337@@.show();
     * ```
     */
    public show(): void {
        this.getOuterElement().show();
    }

    /**
     * Hides the control.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * @@ctr:1337@@.hide();
     * ```
     */
    public hide(): void {
        this.getOuterElement().hide();
    }

    /**
     * Returns true if the control is visible.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * var visible = @@ctr:1337@@.isVisible();
     * ```
     */
    public isVisible(): boolean {
        return this.getOuterElement().is(":visible");
    }

    /**
     * Add a css class to the control.
     * @param cssClass Css class to be added.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * @@ctr:1337@@.addCssClass('highlight1');
     * ```
     */
    public addCssClass(cssClass: string): void {
        let elem = this.getOuterElement();
        elem.addClass(cssClass);
    }

    /**
     * Removes a css class from the control.
     * @param cssClass Css class to be removed.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * @@ctr:1337@@.removeCssClass('highlight1');
     * ```
     */
    public removeCssClass(cssClass: string): void {
        let elem = this.getOuterElement();
        elem.removeClass(cssClass);
    }

    /**
     * Returns true if control has given css class assigned.
     * @since 4.0.0
     * @returns True if the control has given css class.
     * @example
     * ```JavaScript
     * var val = @@ctr:12345@@.hasCssClass('highlight1');
     * ```
     */
    public hasCssClass(cssClass: string): boolean {
        let elem = this.getOuterElement();
        return elem.hasClass(cssClass);
    }

    /**
     * Internal method for setElement.
     * @param element JQueryElement for initialization.
     * @hidden
     */
    protected abstract setElementInternal(element: JQuery): void;

    /**
     * Sets the default properties for the editor.
     * @param element Element where the properties are added.
     * @hidden
     */
    protected setDefaultEditorProperties(element: JQuery): void {
        element.attr(CONTROL_ID_ATTRIBUTE_NAME, this.controlDefinition.ControlID);

        if (this.controlDefinition.Width) {
            element.width(this.controlDefinition.Width);
        }
        if (this.controlDefinition.Height) {
            element.height(this.controlDefinition.Height);
        }
        if (this.controlDefinition.CssClass) {
            element.addClass(this.controlDefinition.CssClass);
        }
        if (this.controlDefinition.IsDefaultButton) {
            element.addClass(CssClass.defaultButton);
        }
    }

    /**
     * Gets template for tooltip
     * @param bindingField
     * @hidden
     */
    protected getTooltipTemplate(bindingField?: string): string {
        return this.getDisplayTemplate(bindingField);
    }

    /**
     * Internal method for getting the template.
     * @param displayMode DisplayMode for control.
     * @param bindingField Field which the control is bound to.
     * @hidden
     */
    protected getTemplateInternal(displayMode: DisplayModes, bindingField?: string): ControlTemplate {

        let controlTemplate = new ControlTemplate();

        if (displayMode == DisplayModes.Display) {
            if (bindingField) {
                controlTemplate.template = this.getDisplayTemplate(`data.get("${bindingField}")`);
                controlTemplate.editable = false;
                return controlTemplate;
            }
            return null;
        }

        // check if control is editable
        let editable = ((this.controlDefinition.OnAddEditable && displayMode == DisplayModes.Add) || (this.controlDefinition.OnEditEditable && displayMode == DisplayModes.Edit));

        // control is not editable
        // render the control readonly with additional classes.
        if (!editable) {

            let element = $(this.defaultEditorElement);

            // add default binding. might be overridden in specific controls
            if (bindingField && this.bindingProperty) {
                element.attr("data-bind", this.bindingProperty + ":" + bindingField + (this.additionalBinding ? ", " + this.additionalBinding : ""));
            }
            element.attr("readonly", "true");
            element.addClass("dms-readonly");

            this.setDefaultEditorProperties(element);

            controlTemplate.template = element[0].outerHTML;
            controlTemplate.element = element;
        }
        // control is editable, render the control as usual.
        else {

            let element = $(this.defaultEditorElement);

            // add default binding. might be overridden in specific controls

            if (bindingField && this.bindingProperty) {
                element.attr("data-bind", this.bindingProperty + ":" + bindingField + (this.additionalBinding ? ", " + this.additionalBinding : ""));
            }
            if (this.controlDefinition.AddRequiredText && displayMode == DisplayModes.Add || this.controlDefinition.EditRequiredText && displayMode == DisplayModes.Edit) {
                element.addClass("dms-required");
            }

            this.setDefaultEditorProperties(element);

            controlTemplate.template = element[0].outerHTML;
            controlTemplate.element = element;
        }
        controlTemplate.editable = editable;
        return controlTemplate;
    }

    /**
     * Generate a wrapper for the input element in the given controlTemplate.
     * This is used for example in CheckboxControl, where the data-binding is on the input-element
     * but the visibility binding is on the wrapper.
     * @param controlTemplate The controltemplate to be modified.
     * @hidden
     */
    protected getInputWrapper(controlTemplate: ControlTemplate): ControlTemplate {
        return controlTemplate;
    }

    /**
     * Gets the editor for DisplayMode and binds the value with databind.
     * @param displayMode Display, Edit or Add
     * @param bindingField Field of the model
     * @hidden
     */
    public getTemplate(displayMode: DisplayModes, bindingField?: string): ControlTemplate {

        // check if control is even visible in this mode
        if (((this.controlDefinition.OnAdd == null || this.controlDefinition.OnAdd) && displayMode == DisplayModes.Add)
            || ((this.controlDefinition.OnEdit == null || this.controlDefinition.OnEdit) && displayMode == DisplayModes.Edit)
            || ((this.controlDefinition.OnDisplay == null || this.controlDefinition.OnDisplay) && displayMode == DisplayModes.Display)
            || (this.controlDefinition["ControlDisplayPermissionID"] == "HIDE") // special case for customisations
        ) {

            let controlTemplate = this.getTemplateInternal(displayMode, bindingField);
            let modelExtension = {};

            if (controlTemplate) {
                controlTemplate = this.getInputWrapper(controlTemplate);
                // wrap display template in span to insert ids, cssClasses, tooltips, etc...
                if (displayMode == DisplayModes.Display) {
                    let cssClass = this.controlDefinition.CssClass || "";
                    let width = "";
                    if (this.controlDefinition.Width) {
                        width = `style="width:${this.controlDefinition.Width}"`;
                    }
                    if (this.controlDefinition.UseValueColumnToolTip && bindingField) {
                        let tooltipTemplate = this.getTooltipTemplate(`DMS.Util.encode(data.get("${bindingField}"), DMS.Enums.EncodingTypes.HtmlAttribute)`);
                        controlTemplate.template = `<span class="dms-control-label dms-tooltip ${cssClass}" ${width} data-dms-tooltip='${tooltipTemplate}'>${controlTemplate.template}</span>`;
                    }
                    else if (this.controlDefinition.ToolTip != null) {
                        let toolTip = this.controlDefinition.ToolTip;
                        if (toolTip.indexOf("#") === -1) {
                            toolTip = `#= DMS.Util.encode(DMS.Service.TextBlock.get("${toolTip}", "${this.textBlockCategory}"), DMS.Enums.EncodingTypes.HtmlAttribute) #`;
                        }
                        // if (toolTip == this.controlDefinition.ToolTip) { // tooltip was not changed in resolving -> tooltip is textblockname
                        //     toolTip = this.textBlockService.get(this.controlDefinition.ToolTip, this.textBlockCategory);
                        // }
                        controlTemplate.template = `<span class="dms-control-label dms-tooltip ${cssClass}" ${width} data-dms-tooltip='${toolTip}'>${controlTemplate.template}</span>`;
                    }
                    else {
                        controlTemplate.template = `<span class="dms-control-label ${cssClass}" ${width}>${controlTemplate.template}</span>`;
                    }
                    controlTemplate.element = $(controlTemplate.template);
                }

                if (this.controlDefinition.RangeColumnName) {
                    // function for model binding
                    let propertyName = CONTROL_VISIBILITY_PROPERTY_NAME + this.controlDefinition.ControlID;
                    let that = this;
                    // create the function
                    $.extend(modelExtension, {
                        [propertyName]: function () {
                            return this.get(DataConstants.ROW_INDEX_COLUMN_NAME) == -1 // range not valid in add mode
                                || Util.isValueInRange(this.get(that.controlDefinition.RangeColumnName), that.controlDefinition.RangeFrom, that.controlDefinition.RangeTo, null, that.controlDefinition.RangeInvert);
                        }
                    });

                    if (displayMode == DisplayModes.Display) { // add the visibilitycondition to the plain template
                        // wrap it with if block
                        controlTemplate.template = `# if(${propertyName}()) { # ${controlTemplate.template} # } #`;
                    }
                    else { // add the visible binding to the control

                        let element = controlTemplate.element;

                        // get already created binding and and prepare for append
                        let dataBinding = element.attr("data-bind");
                        dataBinding = dataBinding ? dataBinding += "," : "";

                        // add visible binding
                        dataBinding += "visible:" + propertyName;
                        element.attr("data-bind", dataBinding);

                        controlTemplate.template = element[0].outerHTML;
                    }
                }

                if (this.controlDefinition.FormatDefinitions && this.controlDefinition.FormatDefinitions.length != 0) {
                    let propertyName = CONTROL_FORMAT_PROPERTY_NAME + this.controlDefinition.ControlID;
                    let formatDefinitions = this.controlDefinition.FormatDefinitions;
                    $.extend(modelExtension, {
                        [propertyName]: function () {
                            return Util.getFormatCondition(this, formatDefinitions);
                        }
                    });

                    let element = controlTemplate.element;
                    let prevElementHtml = element[0].outerHTML;
                    controlTemplate.template = prevElementHtml;
                    if (displayMode == DisplayModes.Display) { // add the visibilitycondition to the plain template
                        element.addClass(`#= ${propertyName}()#`);
                    }
                    else {
                        // get already created binding and and prepare for append
                        let dataBinding = element.attr("data-bind");
                        dataBinding = dataBinding ? dataBinding += "," : "";

                        // add format binding -> use custom binding specified in CustomBinding.ts
                        dataBinding += "cssClass:" + propertyName;
                        element.attr("data-bind", dataBinding);
                    }
                    controlTemplate.template = controlTemplate.template.replace(prevElementHtml, element[0].outerHTML).split("&quot;").join("\"");
                }

                if (controlTemplate.modelExtension == null) {
                    controlTemplate.modelExtension = {};
                }
                $.extend(controlTemplate.modelExtension, modelExtension);

                controlTemplate.template = controlTemplate.template.split("&quot;").join("\"");
            }
            return controlTemplate;
        }
        return null;
    }

    /**
     * Get the template for the grid group header.
     * @hidden
     */
    public getGroupHeaderTemplate() {
        return this.getDisplayTemplate("value");
    }

    /**
     * Get the display template for a binding field.
     * @hidden
     */
    public getDisplayTemplateForField(bindingField: string): string {
        return this.getDisplayTemplate(bindingField);
    }

    /**
     * Gets the display template for formatting in grid row.
     * @hidden
     */
    protected getDisplayTemplate(bindingField?: string): string {
        return `#= ${bindingField} == null ? "${this.controlDefinition.ReplaceNullValue || ""}" : DMS.Util.convertToString(${bindingField}) #`;
    }

    /**
     * Gets the datatype for the model ("string", "number" or "date")
     * @hidden
     */
    public getModelDatatype(): string {
        return "string";
    }

    /**
     * Gets the default value for the model if no default value is set in controlDefinition.
     * @hidden
     */
    public getModelDefaultValue(): any {
        return null;
    }

    /**
     * Plays an animation from animate.css. Check {@link https://daneden.github.io/animate.css/|animate.css} for all available animations.
     * @param animationName Name of animation.
     * @since 4.0.0
     */
    public playAnimation(animationName: string) {
        let elem = this.getOuterElement();

        let animationEnd = "webkitAnimationEnd mozAnimationEnd MSAnimationEnd oanimationend animationend";
        elem.addClass("animated " + animationName).one(animationEnd, function () {
            $(this).removeClass("animated " + animationName);
        });
    }

    /**
     * Sets the focus to this control.
     * @since 4.0.0
     * @example
     * ```
     * @@ctr:1337@@.focus();
     * ```
     */
    public focus(): void {
        this.jQueryElement.focus();
    }

    /**
     * Hide the grid column containing this control.
     * @since 4.0.0
     */
    public hideGridColumn(): void {
    }

    /**
     * Get the parsed default value for this control.
     * @hidden
     */
    public getDefaultValue(): Object {
        let dataType = this.getModelDatatype();

        if (!Util.stringIsNullOrEmpty(this.controlDefinition.DefaultValue)) {
            let values = this.controlDefinition.DefaultValue.split(";");
            for (let i = 0, len = values.length; i < len; i++) {
                let defaultValue = Util.resolve(values[i], {});
                let value = Util.parse(dataType, defaultValue, "en-US");
                if (value == null) {
                    value = Util.parse(dataType, defaultValue, "de"); // try parsing with german culture
                }
                if (value == null) {
                    value = Util.parse(dataType, defaultValue); // try parsing with default culture
                }
                if (value != null) {
                    return value;
                }
            }
        }
        return this.getModelDefaultValue();
    }

    /**
     * Modify the control definition with the current datarow.
     * This enables changes which are made via customisation.
     * @hidden
     */
    public modifyForDataRow(dataRow: IDataRow): void {
        if ((this.controlDefinition.Customisations && this.controlDefinition.Customisations.length > 0)
            || (this.controlDefinition.EditRangeColumnName)) {
            // copy the original controldefinition and restore it each time this method is called.
            if (this.originalControlDefinition == null) {
                this.originalControlDefinition = this.controlDefinition;
            }
            this.controlDefinition = $.extend({}, this.originalControlDefinition);
        }

        if (this.controlDefinition.Customisations && this.controlDefinition.Customisations.length > 0) {
            for (let ctsIdx = 0, ctsLen = this.controlDefinition.Customisations.length; ctsIdx < ctsLen; ctsIdx++) {
                let customisation = this.controlDefinition.Customisations[ctsIdx];
                if (Util.isValueInRange(dataRow[customisation.RangeColumnName], customisation.RangeFrom, customisation.RangeTo)) {
                    this.controlDefinition[customisation.Property] = Util.parse(typeof this.controlDefinition[customisation.Property] || "string", customisation.Value);
                }
            }
        }
        if (this.controlDefinition.EditRangeColumnName) {
            this.controlDefinition.OnEditEditable = Util.isValueInRange(dataRow[this.controlDefinition.EditRangeColumnName], this.controlDefinition.EditRangeFrom, this.controlDefinition.EditRangeTo, null, this.controlDefinition.EditRangeInvert);
        }
    }

    /**
     * Set the height of the control.
     * **Only implemented for CustomControl**.
     * @param height Height in Pixel.
     */
    public setHeight(height: number) {
    }

    /**
     * Gets the outer element. This is either the HTMLElement or a wrapper added by the control.
     * @since 4.0.0
     * @hidden
     */
    public getOuterElement(): JQuery {
        return this.jQueryElement;
    }

    /**
     * Dispose the control and release all resources.
     * @since 4.0.0
     */
    public dispose(): void {
        this.off(); // remove all eventhandlers
    }
}
